import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  GET_HOST_WITH_ALERTS,
  GET_VISIBILITY,
  GET_CRITICALITY,
  sensitivityMap,
  MonitorMap,
  desiredOrderAlerts,
  desiredOrderMonitor,
  desiredOrderCriticality,
  criticalityBGColorMap,
  criticalityMap,
} from "./NetSpaceConstant";
import Page_Loader from "../../assets/vizr_images/page_loader.gif";
import { Grid } from "material-ui-core";
import CardContent from "@material-ui/core/CardContent";
import {
  alertColor,
  sensitivityColorDark1,
  StyledCardHeader,
} from "./NetSpaceUtils";
import { CustomCard } from "./NetSpaceCommonComp";
import { PieChart, Pie, Tooltip, Cell, ResponsiveContainer } from "recharts";
import { sensitivityColor } from "./NetSpaceUtils";
import { percentageCalculation } from "../../components/common/utilities";
import { useMediaQuery } from "@material-ui/core";

function CombinedHostWidget() {
  const dispatch = useDispatch();
  const isSmall = useMediaQuery("(max-width:1600px)");
  const isMedium = useMediaQuery("(max-width:1800px)");

  useEffect(() => {
    dispatch({ type: GET_HOST_WITH_ALERTS });
    dispatch({ type: GET_VISIBILITY });
    dispatch({ type: GET_CRITICALITY });
  }, [dispatch]);

  const hostWithAlert = useSelector(
    (state) => state.NetspaceReducer.hostWithAlert
  );
  const visibility = useSelector((state) => state.NetspaceReducer.visibility);
  const criticality = useSelector((state) => state.NetspaceReducer.criticality);

  const loaderAlert = useSelector(
    (state) => state.NetspaceReducer.componentLoader[GET_HOST_WITH_ALERTS]
  );
  const loaderVisibility = useSelector(
    (state) => state.NetspaceReducer.componentLoader[GET_VISIBILITY]
  );
  const loaderCriticality = useSelector(
    (state) => state.NetspaceReducer.componentLoader[GET_CRITICALITY]
  );

  const totalAlerts = hostWithAlert
    ? Object.values(hostWithAlert).reduce((acc, curr) => acc + curr, 0)
    : 0;
  const totalVisibility = visibility
    ? Object.values(visibility).reduce((acc, curr) => acc + curr, 0)
    : 0;
  const totalCriticality = criticality
    ? desiredOrderCriticality.reduce(
        (acc, type) => acc + (criticality[type] || 0),
        0
      )
    : 0;
  
  const alertData = desiredOrderAlerts
    .filter((alert) =>
      alert === "INFORMATIONAL" ? alert in (hostWithAlert || {}) : true,
    )
    .map((alert) => ({
      name: `${sensitivityMap[alert]} Alert`,
      value:
        alert === "INFORMATIONAL"
          ? hostWithAlert[alert]
          : hostWithAlert?.[alert] || 0,
      color: alertColor(alert),
    }));

  const visibilityData = desiredOrderMonitor.map((monitor) => ({
    name: `${MonitorMap[monitor]} Visibility`,
    value: visibility?.[monitor] || 0,
    color: sensitivityColorDark1(monitor),
  }));

  // helper function
  const getSize = (small, medium, normal) => {
    if (isSmall) return small;
    if (isMedium) return medium;
    return normal;
  };

  let criticalityData = [];
  if (criticality) {
    criticalityData = desiredOrderCriticality.map((type) => {
      return {
        name: `${criticalityMap[type]} Criticality`,
        value: criticality[type] || 0,
        color: criticalityBGColorMap[type],
      };
    });
  }

  return (
    <Grid xs={4} item>
      <CustomCard color="white">
        <StyledCardHeader title={`Host Overview:`} />
        <CardContent>
          {loaderAlert || loaderVisibility || loaderCriticality ? (
            <img src={Page_Loader} alt="loading" className="loader-img" />
          ) : (
            <div className="combined-host-widget">
              <a
                className="a-tag-none"
                href="https://nodezero.druvstar.net/investigate/hosts"
                target="_blank"
                rel="noopener noreferrer"
              ></a>
              <div className="combined-host-widget__text">
                <div className="text-info-container">
                  <span
                    className="text-info-title"
                    title={`Alerts: ${totalAlerts}`}
                  >{`Alerts: ${totalAlerts}`}</span>
                  {alertData.map((item, index) => (
                    <div
                      key={index}
                      className="text-ellipsis"
                      style={{ color: item.color }}
                    >
                      {item.value} {item.name.replace(" Alert", "")}
                    </div>
                  ))}
                </div>
                <div className="text-info-container">
                  <span
                    className="text-info-title"
                    title={`Visibility: ${totalVisibility}`}
                  >{`Visibility: ${totalVisibility}`}</span>
                  {visibilityData.map((item, index) => (
                    <div
                      key={index}
                      className="text-ellipsis"
                      style={{ color: item.color }}
                    >
                      {item.value} {item.name.replace(" Visibility", "")}
                    </div>
                  ))}
                </div>
                <div className="text-info-container">
                  <span
                    className="text-info-title"
                    title={`Critical Ports: ${totalCriticality}`}
                  >{`Critical Ports: ${totalCriticality}`}</span>
                  {criticalityData.map((item, index) => (
                    <div
                      key={index}
                      className="text-ellipsis"
                      style={{ color: item.color }}
                    >
                      {item.value} {item.name.replace(" Criticality", "")}
                    </div>
                  ))}
                </div>
              </div>
              <div className="combined-host-widget__chart">
                <ResponsiveContainer width={120} height={120}>
                  <PieChart>
                    <Pie
                      data={alertData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={getSize(18, 21, 24)} // small=18, medium=21, normal=24
                    >
                      {alertData.map((entry, index) => (
                        <Cell key={`v-cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>

                    <Pie
                      data={visibilityData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={getSize(20, 25, 30)}
                      outerRadius={getSize(30, 35, 40)}
                    >
                      {visibilityData.map((entry, index) => (
                        <Cell key={`a-cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>

                    <Pie
                      data={criticalityData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={getSize(36, 41, 46)}
                      outerRadius={getSize(46, 51, 56)}
                    >
                      {criticalityData.map((entry, index) => (
                        <Cell key={`c-cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>

                    <Tooltip
                      content={({ active, payload }) =>
                        active && payload?.length ? (
                          <div
                            style={{
                              background: "#fff",
                              padding: "5px",
                              border: "1px solid #ccc",
                              fontSize: getSize(10, 11, 12), // 👈 responsive tooltip text
                            }}
                          >
                            {payload.map((entry, idx) => (
                              <div key={idx}>
                                {entry.name}: {entry.value}
                              </div>
                            ))}
                          </div>
                        ) : null
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </CardContent>
      </CustomCard>
    </Grid>
  );
}

export default CombinedHostWidget;
